import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { PlaySessionType } from '../../types/PlaySession.type';
import { PlaySessionContext, PlaySessionDispatch } from './context';
import { usePlaySessionContext } from './usePlaySessionContext';
import { usePlaySessionDispatch } from './usePlaySessionDispatch';
import { useNavigate } from 'react-router-dom';
import { HttpClient } from '../../apis/http-client';
import { LegacyRealtime } from '../../realtime/legacyMqtt';
import { createSignalHandler, mergeTeam, mergeUser } from '../../realtime/sessionMerge';

/*
 * ValueInvestor(2026-09-16): 원본의 socket.io `SocketListner` 를 MQTT 기반 `RealtimeListener` 로 바꿨다.
 * 이 파일이 원본 클라이언트에서 **유일하게 동작을 고친 파일**이다(FE-ADOPT-PLAN 3단계 금지 1).
 *
 *   원본                         → 지금
 *   BROADCAST(문서 전체)          → 토픽 신호(state·stocks·team·user) + 조각 재조회 후 문서에 병합
 *   SEND_MY_INFO · KEEP_ALIVE    → POST /game/keep-alive (20초 주기, 원본과 같음)
 *   BAN_USER                     → user 토픽의 { type: 'ban' } 신호
 *   (없음)                        → 쓰기 API 응답의 `__signals` 를 그대로 발행(팀원·현황판 갱신, 서버는 발행하지 않는다)
 */

interface PlaySessionProviderProps {
  initialSession: PlaySessionType;
  teamCode: string;
  userId?: string;
  children?: any;
}

const KEEP_ALIVE_MS = 20 * 1000;
/** PC 대기 화면 "접속 완료 N/M" 갱신 주기 — 입장은 로그인 시점이라 신호가 없다(Vue 판 D-42 와 같은 처리) */
const ROSTER_POLL_MS = 5 * 1000;
/** 단계 만료 뒤 이만큼 지나도 신호가 없으면 스스로 읽는다 — 엔진 발행 유실 대비(Vue 판과 같음) */
const EXPIRY_GRACE_MS = 5 * 1000;

const RealtimeListener: FC = () => {
  const { teamCode, userId, session } = usePlaySessionContext();
  const { dispatch } = usePlaySessionDispatch();
  const navigate = useNavigate();
  // 효과 안에서 최신 단계·만료 시각을 읽기 위한 참조(효과는 userId 로만 다시 걸린다)
  const latest = useRef(session);
  latest.current = session;

  useEffect(() => {
    const realtime = new LegacyRealtime();
    let disposed = false;
    let keepAlive: ReturnType<typeof setInterval> | null = null;
    let watchdog: ReturnType<typeof setInterval> | null = null;
    const sessionId = session._id;

    // 내 팀 — MO 는 사용자로, PC 는 팀 코드로 찾는다
    const myTeamId = (): string | null => {
      if (userId) return session.users.find((u) => u._id === userId)?.team_id ?? null;
      return session.teams.find((t) => t.code === teamCode)?._id ?? null;
    };

    const merge = (patch: (prev: PlaySessionType) => PlaySessionType) => {
      if (disposed) return;
      dispatch((prev) => ({ ...prev, session: patch(prev.session) }));
    };

    async function refetchFull() {
      try {
        const { data } = await HttpClient.get<PlaySessionType>('/game/session', { params: { sessionId } });
        merge(() => data);
      } catch (e) {
        console.error('[realtime] 세션 문서 재조회 실패 — sessionId =', sessionId, e);
      }
    }
    async function refetchState() {
      try {
        const { data } = await HttpClient.get('/game/session/state', { params: { sessionId } });
        merge((prev) => ({ ...prev, ...data }));
      } catch (e) {
        console.error('[realtime] state 조각 재조회 실패 — sessionId =', sessionId, e);
      }
    }
    async function refetchStocks() {
      try {
        const { data } = await HttpClient.get('/game/session/stocks', { params: { sessionId } });
        merge((prev) => ({ ...prev, stocks: data }));
      } catch (e) {
        console.error('[realtime] stocks 조각 재조회 실패 — sessionId =', sessionId, e);
      }
    }
    async function refetchTeam() {
      const teamId = myTeamId();
      if (!teamId) return refetchFull();
      try {
        const { data } = await HttpClient.get(`/game/session/team/${teamId}`, { params: { sessionId } });
        merge((prev) => mergeTeam(prev, teamId, data));
      } catch (e) {
        console.error('[realtime] team 조각 재조회 실패 — teamId =', teamId, e);
      }
    }
    async function refetchUser() {
      if (!userId) return;
      try {
        const { data } = await HttpClient.get(`/game/session/user/${userId}`, { params: { sessionId } });
        merge((prev) => mergeUser(prev, userId, data));
      } catch (e) {
        console.error('[realtime] user 조각 재조회 실패 — userId =', userId, e);
      }
    }

    function onBanUser() {
      sessionStorage.removeItem('LastLoginClientId');
      sessionStorage.removeItem('LastLoginInfo');
      navigate('/');
    }

    const signals = createSignalHandler({
      current: () => latest.current,
      merge,
      refetchFull,
      refetchState,
      refetchStocks,
      refetchTeam,
      refetchUser,
      onBan: onBanUser,
    });

    // 쓰기 API 응답의 `__signals` 를 발행한다 — 서버(Lambda)는 MQTT 에 붙지 않으므로 클라이언트가 한다
    const interceptor = HttpClient.interceptors.response.use((res) => {
      const found = (res?.data as { __signals?: { topicKey: string; payload: Record<string, unknown> }[] } | null)?.__signals;
      if (Array.isArray(found)) {
        for (const s of found) realtime.publish(s.topicKey, s.payload);
      }
      return res;
    });

    async function sendKeepAlive() {
      if (!userId) return;
      try {
        await HttpClient.post('/game/keep-alive', { sessionId });
      } catch (e) {
        console.error('[realtime] keep-alive 실패 — sessionId =', sessionId, e);
      }
    }

    void realtime.connect(sessionId, {
      onSignal: (key, payload) => signals.handle(key, payload),
      // 재연결 뒤에는 끊긴 동안 놓친 변화를 모른다 — 문서를 통째로 다시 읽는다(FE-ADOPT-PLAN C2)
      onConnected: () => {
        void refetchFull();
      },
    });
    void sendKeepAlive();
    keepAlive = setInterval(() => void sendKeepAlive(), KEEP_ALIVE_MS);

    // 신호가 올 수 없는 두 자리를 1초 감시로 메운다(원본은 소켓으로 문서 전체를 계속 받던 자리)
    let lastRosterFetch = 0;
    let refetchedFor: string | null = null;
    watchdog = setInterval(() => {
      const cur = latest.current;
      const now = Date.now();
      if (!userId && cur.current_step === 'wait') {
        if (now - lastRosterFetch >= ROSTER_POLL_MS) {
          lastRosterFetch = now;
          void refetchFull();
        }
        return;
      }
      const expireAt = cur.currentStepExpiryDate;
      if (!expireAt || refetchedFor === expireAt) return;
      if (now - new Date(expireAt).getTime() >= EXPIRY_GRACE_MS) {
        refetchedFor = expireAt;
        // 신호를 놓친 경우라 턴 경계인지 알 수 없다 — 문서 전체를 읽는다(sessionMerge 의 턴 경계 처리와 같은 이유)
        void refetchFull();
      }
    }, 1000);

    return () => {
      disposed = true;
      HttpClient.interceptors.response.eject(interceptor);
      realtime.end();
      signals.dispose();
      if (keepAlive) clearInterval(keepAlive);
      if (watchdog) clearInterval(watchdog);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return null;
};

export const PlaySessionProvider: FC<PlaySessionProviderProps> = ({
  initialSession,
  teamCode,
  children,
  userId,
}) => {
  const lastDate = useRef<Date | null>(null);
  const [playSession, setPlaySession] = useState<{
    session: PlaySessionType;
    teamCode: string;
    userId?: string;
  }>({
    session: initialSession,
    teamCode: teamCode,
    userId,
  });

  const dispatch = useMemo(() => {
    return {
      setPlaySession: (next: PlaySessionType, createdAt: string) => {
        const sendCreatedAt = new Date(createdAt);
        if (
          lastDate.current === null ||
          (isNaN(sendCreatedAt.getTime()) === false &&
            sendCreatedAt > lastDate.current)
        ) {
          lastDate.current = new Date(createdAt);
          setPlaySession((prev) => ({ ...prev, session: next }));
        }
      },
      dispatch: setPlaySession,
    };
  }, []);

  return (
    <PlaySessionContext.Provider
      value={{
        ...playSession,
        is_effect_sound: playSession.session.is_effect_sound, // 컨텍스트 값에 추가
        round_2_start: playSession.session.round_2_start, // session 객체에서 round_2_start 추출
      }}
    >
      <PlaySessionDispatch.Provider value={dispatch}>
        <RealtimeListener />
        {children}
      </PlaySessionDispatch.Provider>
    </PlaySessionContext.Provider>
  );
};
